import React from "react";
import {
  Card,
  Grid,
  Typography,
  Theme,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material";
import {
  AccountBalanceWallet as BudgetIcon,
  TrendingUp as ForecastIcon,
  AccountTree as ProjectsIcon,
  Assessment as TCQIIcon,
} from "@mui/icons-material";
import { ProgrammeSummaryMetric } from "../types";
import { KpiCard } from "./KpiCard";

interface ProgrammeSummaryProps {
  metrics: ProgrammeSummaryMetric[];
  isLoading?: boolean;
  isError?: boolean;
  error?: string;
}

const getIcon = (label: string) => {
  if (label.includes("Budget")) return <BudgetIcon fontSize="large" />;
  if (label.includes("Forecast")) return <ForecastIcon fontSize="large" />;
  if (label.includes("Project")) return <ProjectsIcon fontSize="large" />;
  if (label.includes("TCQI")) return <TCQIIcon fontSize="large" />;
  return <BudgetIcon fontSize="large" />;
};

const getIconColor = (label: string, theme: Theme) => {
  if (label.includes("Budget")) return theme.palette.primary.main;
  if (label.includes("Forecast")) return theme.palette.info.main;
  if (label.includes("Project")) return theme.palette.success.main;
  if (label.includes("TCQI")) return theme.palette.warning.main;
  return theme.palette.primary.main;
};

export const ProgrammeSummary: React.FC<ProgrammeSummaryProps> = ({
  metrics,
  isLoading = false,
  isError = false,
  error,
}) => {
  const theme = useTheme();

  return (
    <Card variant="outlined" sx={{ p: 0 }}>
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "action.hover",
        }}
      >
        <Typography variant="subtitle2">Summary</Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error">
            {error || "Failed to load portfolio summary"}
          </Alert>
        ) : metrics.length > 0 ? (
          <Grid container spacing={2}>
            {metrics.map((metric, index) => (
              <Grid size={{ xs: 12, sm: 6 }} key={index}>
                <KpiCard
                  label={metric.label}
                  value={metric.value}
                  icon={getIcon(metric.label)}
                  iconColor={getIconColor(metric.label, theme)}
                  subValue={metric.subValue}
                />
              </Grid>
            ))}
          </Grid>
        ) : null}
      </Box>
    </Card>
  );
};
